import { Mic, Pause, Square, Play } from 'lucide-react';

import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';

interface RecordingDetailsBoxProps {
  isRecording: boolean;
  isPaused: boolean;
  isConnected: boolean;
  caseId: string | null;
  onStartRecording: () => void;
  onPauseRecording: () => void;
  onCompleteRecording: () => void;
}

export const RecordingDetailsBox = ({
  isRecording,
  isPaused,
  isConnected,
  caseId,
  onStartRecording,
  onPauseRecording,
  onCompleteRecording,
}: RecordingDetailsBoxProps) => {
  const getStatusText = () => {
    if (!isConnected) return 'Disconnected';
    if (isPaused) return 'Paused';
    if (isRecording) return 'Recording';
    return 'Ready';
  };

  const getStatusColor = () => {
    if (!isConnected) return { text: 'text-red-600', bg: 'bg-red-600' };
    if (isPaused) return { text: 'text-yellow-600', bg: 'bg-yellow-600' };
    if (isRecording) return { text: 'text-green-600', bg: 'bg-green-600' };
    return { text: 'text-gray-600', bg: 'bg-gray-600' };
  };

  return (
    <div className="flex items-center gap-2">
      <div className={`flex h-2 w-2 items-center gap-2 rounded-full ${getStatusColor().bg}`} />
      <div className={`mr-2 text-sm font-medium ${getStatusColor().text}`}>{getStatusText()}</div>
      {/* Play/Pause button */}
      {!isRecording || isPaused ? (
        <button
          onClick={() => {
            trackCustomEvent(TrackingEvents.StartRecording);
            onStartRecording();
          }}
          disabled={!isConnected || !caseId}
          className="flex h-6 w-6 items-center justify-center rounded-md border border-green-300 bg-green-50 text-green-700 transition-colors hover:bg-green-100 disabled:cursor-not-allowed disabled:opacity-40"
          title={isPaused ? 'Resume' : 'Start Recording'}
        >
          {isPaused ? <Play className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
        </button>
      ) : (
        <button
          onClick={() => {
            trackCustomEvent(TrackingEvents.PauseRecording);
            onPauseRecording();
          }}
          className="flex h-6 w-6 items-center justify-center rounded-md border border-amber-300 bg-amber-50 text-amber-700 transition-colors hover:bg-amber-100"
          title="Pause Recording"
        >
          <Pause className="h-3.5 w-3.5" />
        </button>
      )}

      {/* Stop button - show when recording or paused */}
      {(isRecording || isPaused) && (
        <button
          onClick={() => {
            trackCustomEvent(TrackingEvents.StopRecording);
            onCompleteRecording();
          }}
          className="flex h-6 w-6 items-center justify-center rounded-md border border-red-300 bg-red-50 text-red-700 transition-colors hover:bg-red-100"
          title="Stop Recording"
        >
          <Square className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
};
